import { formatDate, formatMoney, useLocale, useTranslation } from '@multaqa/i18n'
import { DateField } from '@multaqa/ui/components/ui/date-field'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { SourceDocumentLink } from '@/components/journal-entry-source'
import { TableScroll } from '@/components/table-scroll'
import { companyTodayIsoDate } from '@/lib/company-date'
import { documentTypeLabel } from '@/lib/document-type-label'
import { meQuery } from '@/lib/me'
import {
  type CustomerStatement,
  type StatementType,
  useCustomerStatement,
  useStatementDefaultType,
} from '../hooks/use-customer-accounting'
import { getCustomerPanelState } from './customer-accounting-panels'

type StatementRange = { start: string; end: string }

// 1 January of the company's current year through today, in the company's own date.
function defaultStatementRange(): StatementRange {
  const today = companyTodayIsoDate()
  return { start: `${today.slice(0, 4)}-01-01`, end: today }
}

export function CustomerStatementPanel({ customerId }: { customerId: string }) {
  const { t } = useTranslation()
  // The document-type labels are the Statements screen's, so they come from its namespace.
  const { t: tAccounting } = useTranslation('accounting')
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const defaultType = useStatementDefaultType(me?.active_company_id ?? null)
  const type = (defaultType.data ?? 'balance_forward') as StatementType
  const [range, setRange] = useState(defaultStatementRange)
  const statement = useCustomerStatement(
    me?.active_company_id ?? null,
    customerId,
    type,
    range.start,
    range.end,
  )

  if (!me?.active_company_id) return null
  const statementData = statement.data
  const panelState = getCustomerPanelState({
    panel: 'statement',
    loading: statement.isLoading,
    error: statement.isError,
    empty:
      !statementData ||
      (statementData.rows.length === 0 && statementData.summary?.opening_balance === undefined),
    columns: [2, 2, 3, 2, 2, 2],
    skeletonRows: 5,
    onRetry: () => void statement.refetch(),
    t,
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-label text-muted-foreground tabular-nums">
            {t('customer.hub.statementFrom')}
          </span>
          <DateField
            id="customer-statement-from"
            aria-label={t('customer.hub.statementFrom')}
            data-testid="customer-statement-from"
            value={range.start}
            max={range.end}
            onChange={(next) => {
              if (next) setRange({ ...range, start: next })
            }}
            locale={locale}
            calendarLabel={t('common:date.openCalendar')}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-label text-muted-foreground tabular-nums">
            {t('customer.hub.statementTo')}
          </span>
          <DateField
            id="customer-statement-to"
            aria-label={t('customer.hub.statementTo')}
            data-testid="customer-statement-to"
            value={range.end}
            min={range.start}
            onChange={(next) => {
              if (next) setRange({ ...range, end: next })
            }}
            locale={locale}
            calendarLabel={t('common:date.openCalendar')}
          />
        </div>
      </div>
      {panelState ??
        (statementData ? (
          <StatementTable
            statement={statementData}
            range={range}
            locale={locale}
            t={t}
            tAccounting={tAccounting}
          />
        ) : null)}
    </div>
  )
}

function StatementTable({
  statement,
  range,
  locale,
  t,
  tAccounting,
}: {
  statement: CustomerStatement
  range: StatementRange
  locale: 'en' | 'ar'
  t: (key: string) => string
  tAccounting: (key: string, options?: Record<string, unknown>) => string
}) {
  const { columns } = statement.header
  const openingBalance = statement.summary?.opening_balance
  // Only the date, description and running-balance cells carry the opening line; the rest stay empty.
  const openingCells: Record<string, string> = {
    date: formatDate(new Date(range.start), locale),
    description: t('customer.hub.openingBalance'),
    running_balance: formatMoney(openingBalance ?? '0', statement.currency, locale),
  }

  return (
    <TableScroll>
      <Table data-testid="customer-statement-table">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            {columns.map((column) => (
              <TableHead key={column} className={isMoneyColumn(column) ? 'text-end' : undefined}>
                {statementColumnLabel(column, t)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {openingBalance !== undefined ? (
            <TableRow data-testid="customer-statement-opening-balance">
              {columns.map((column) => (
                <TableCell
                  key={column}
                  className={isMoneyColumn(column) ? 'text-end tabular-nums' : ''}
                >
                  {openingCells[column] ?? ''}
                </TableCell>
              ))}
            </TableRow>
          ) : null}
          {statement.rows.map((row, index) => (
            <TableRow key={row.number ?? `${row.date}-${index}`}>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  className={isMoneyColumn(column) ? 'text-end tabular-nums' : ''}
                >
                  {statementCell(column, row, statement.currency, locale, tAccounting)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  )
}

function isMoneyColumn(key: string) {
  return key === 'amount' || key === 'paid' || key === 'open_balance' || key === 'running_balance'
}

function statementColumnLabel(key: string, t: (key: string) => string) {
  const labels: Record<string, string> = {
    date: 'date',
    type: 'type',
    number: 'document',
    due_date: 'dueDate',
    description: 'description',
    amount: 'amount',
    paid: 'paid',
    open_balance: 'balance',
    running_balance: 'balance',
  }
  return t(`customer.hub.columns.${labels[key] ?? 'document'}`)
}

function statementCell(
  key: string,
  row: Record<string, string | null>,
  currency: string,
  locale: 'en' | 'ar',
  tAccounting: (key: string, options?: Record<string, unknown>) => string,
) {
  const value = row[key]
  if (isMoneyColumn(key)) return formatMoney(value ?? '0', currency, locale)
  if (key === 'type') return documentTypeLabel(value ?? null, tAccounting)
  if (key === 'number') {
    // A line opens the document that posted it, where there is one.
    return (
      <SourceDocumentLink sourceType={row.source_type} sourceId={row.source_id}>
        {value ? <bdi dir="ltr">{value}</bdi> : '—'}
      </SourceDocumentLink>
    )
  }
  if (key === 'date' || key === 'due_date') {
    return value ? formatDate(new Date(value), locale) : '—'
  }
  return value ?? '—'
}
